/**
 * IVR Journey Service
 *
 * Service layer for fetching IVR Journey data from the API
 * Handles API communication, response validation, and model transformation
 */

import {
  IVRCallJourney,
  IVRCallJourneyModel,
  IVRJourneyResponseWrapper
} from "src/models/ivrJourney";
import { erebus } from "boot/axios";

/**
 * Service class for IVR Journey API operations
 */
export default class IVRJourneyService {
  /**
   * Fetch IVR journey for a specific call ID
   * @param callID - UUID of the call
   * @returns Promise resolving to array of IVR call journeys
   * @throws Error if API request fails or data validation fails
   */
  async fetchJourneyByCallID(callID: string): Promise<IVRCallJourney[]> {
    try {
      const response = await erebus.get<IVRJourneyResponseWrapper>("/ivr/journeys", {
        params: { callID }
      });

      if (!response.data || !response.data.journeys) {
        return [];
      }

      // Transform API response to model instances
      return response.data.journeys.map(journeyData =>
        new IVRCallJourneyModel(journeyData)
      );
    } catch (error) {
      console.error("Error fetching IVR journey by call ID:", error);
      throw new Error(`Failed to fetch IVR journey for call ${callID}: ${error}`);
    }
  }

  /**
   * Fetch IVR journeys for all calls in a specific activation
   * @param activationID - Numeric ID of the activation
   * @returns Promise resolving to array of IVR call journeys
   * @throws Error if API request fails or data validation fails
   */
  async fetchJourneysByActivationID(activationID: number): Promise<IVRCallJourney[]> {
    try {
      const response = await erebus.get<IVRJourneyResponseWrapper>("/ivr/journeys", {
        params: { activationID }
      });

      if (!response.data || !response.data.journeys) {
        return [];
      }

      // Transform API response to model instances
      return response.data.journeys.map(journeyData =>
        new IVRCallJourneyModel(journeyData)
      );
    } catch (error) {
      console.error("Error fetching IVR journeys by activation ID:", error);
      throw new Error(`Failed to fetch IVR journeys for activation ${activationID}: ${error}`);
    }
  }

  /**
   * Fetch signed URL for a recording
   * @param recordingID - UUID of the recording
   * @returns Promise resolving to signed URL string
   * @throws Error if API request fails
   */
  async fetchRecordingURL(recordingID: string): Promise<string> {
    try {
      const response = await erebus.get<{ url: string }>(`/ivr/recordings/${recordingID}`);

      if (!response.data || !response.data.url) {
        throw new Error("Invalid response: missing URL");
      }

      return response.data.url;
    } catch (error) {
      console.error("Error fetching recording URL:", error);
      throw new Error(`Failed to fetch recording URL for ${recordingID}: ${error}`);
    }
  }
}
